/**
 * The vocabulary linter, and the presentation invariant
 *
 * 00-INDEX.md § Truth-telling: nothing is hidden from the user, but *we* do not
 * ship a rule that pronounces and offers nothing. Two checks live here.
 *
 * 1. **A forbidden-vocabulary linter** over rule files we author. Profiles:
 *    `strict` (legacy soft ban), `medical` (no certainty/finality), `open`
 *    (product — fortune / medical / death phala allowed). Composition for the
 *    sold auto-reading uses `open`.
 *
 * 2. **`assertPresentable`** — the invariant
 *
 *        class = negative  ⇒  the reading MUST carry ≥ 1 parihāra
 *
 *    Enforced by throwing. A renderer calls it before drawing a reading, so a
 *    rule that pronounces without offering a classical response cannot reach
 *    the page even if the loader is bypassed.
 *
 * ◆ Two profiles, because the medical rules must say "disease" to be about
 * health at all. Their ban is on *certainty and finality* ("will die",
 * "incurable"), not on the subject.
 *
 * © Manish Jagdish Thatte
 */

/** Yogas and doṣas: no lifespan, disease, death, or loss-of-spouse language at all. */
export const FORBIDDEN_STRICT = Object.freeze([
  /\b(death|deaths|dead|die|dies|died|dying|deadly|fatal|fatality|lifespan|life-span|longevity|long-lived|short-lived|mortal|mortality)\b/i,
  /\b(disease|diseases|diseased|illness|illnesses|ill-health|sick|sickness|ailment|ailments|cancer|tumou?rs?|infirm|infirmity)\b/i,
  /\b(accident|accidents|injur(?:y|ies|ed)|widow|widowed|widowhood|barren|infertile|divorce|suicide|murder|kill|kills|killed|killer|killing)\b/i,
  /\b(doom|doomed|curse|cursed|ruin|ruined|calamity|catastrophe|catastrophic|misery|wretched|imprisoned|imprisonment|poverty|destitute|poor)\b/i,
  /\bwill (?:suffer|die|lose|fail|be ruined)\b/i
]);

/** Medical rules: the subject is allowed; finality and certainty are not. */
export const FORBIDDEN_MEDICAL = Object.freeze([
  /\b(death|die|dies|died|dying|fatal|terminal|incurable|doomed|cancer|tumou?rs?)\b/i,
  /\bwill (?:suffer|die|develop|contract|get|have)\b/i,
  /\b(?:certainly|definitely|guaranteed|inevitabl[ey]|is destined)\b/i
]);

/**
 * Product profile (owner directive 29 Sep 2026): fortune / medical / death
 * language is allowed in authored rules and composed readings. `strict` and
 * `medical` remain for optional linting and older tests.
 */
export const FORBIDDEN_OPEN = Object.freeze([]);

export const PROFILES = Object.freeze({
  strict: FORBIDDEN_STRICT,
  medical: FORBIDDEN_MEDICAL,
  open: FORBIDDEN_OPEN,
});

/** The terms found in `text`, under a profile. Empty array means clean. */
export function lintText(text, profile = 'strict') {
  const rules = PROFILES[profile];
  if (!rules) throw new RangeError(`lintText: unknown profile "${profile}"`);
  const hits = [];
  for (const re of rules) {
    const m = String(text).match(new RegExp(re.source, 'gi'));
    if (m) hits.push(...m.map((s) => s.toLowerCase()));
  }
  return hits;
}

/**
 * Lint every string reachable from `value`, ignoring keys that start with `_`
 * (schema notes) and the keys in `skipKeys`.
 *
 * Returns `[{ path, term }]`. Callers throw or assert on non-empty.
 */
export function lintObject(value, profile = 'strict', { skipKeys = [] } = {}) {
  const out = [];
  const skip = new Set(skipKeys);
  (function walk(v, path) {
    if (typeof v === 'string') {
      for (const term of lintText(v, profile)) out.push({ path, term });
    } else if (Array.isArray(v)) {
      v.forEach((x, i) => walk(x, `${path}[${i}]`));
    } else if (v && typeof v === 'object') {
      for (const [k, x] of Object.entries(v)) {
        if (k.startsWith('_') || skip.has(k)) continue;
        walk(x, path ? `${path}.${k}` : k);
      }
    }
  })(value, '');
  return out;
}

export class PresentationError extends Error {
  constructor(message, reading) {
    super(message);
    this.name = 'PresentationError';
    this.reading = reading ?? null;
  }
}

/**
 * The renderer's gate. A reading is `{ id, class, parihara: [...] }`.
 *
 * `class` is 'positive' | 'mixed' | 'negative'. Only 'negative' is constrained,
 * because that is the class that would otherwise leave a reader with a
 * pronouncement and nothing to do about it.
 */
export function assertPresentable(reading) {
  if (!reading || typeof reading !== 'object') {
    throw new PresentationError('assertPresentable: no reading given');
  }
  if (reading.class === 'negative') {
    const list = reading.parihara;
    const ok = Array.isArray(list) && list.some((p) => (typeof p === 'string' ? p : p?.text)?.trim());
    if (!ok) {
      throw new PresentationError(
        `"${reading.id ?? 'unnamed'}" is classed negative but carries no parihāra. ` +
        'A reading that names a difficulty must also name the classical measure that answers it.',
        reading
      );
    }
  }
  return reading;
}
